import {useRef, useMemo} from 'react';
import {
  EngraveSettings,
  StrapDimensionsDefaults,
  ValidationResult,
  ZoomLevel,
  ZOOM_SCALE_VALUE,
  getScaledStrapDimensions
} from '../types';
import {useCanvasPreview} from '../hooks/useCanvasPreview';

interface EngravePreviewProps {
  settings: EngraveSettings;
  isFrontFace?: boolean;
  zoomLevel?: ZoomLevel;
  onValidationChange?: (result: ValidationResult) => void;
}

/**
 * 彫刻プレビューコンポーネント
 * Canvas上に彫刻のプレビューを表示
 */
export const EngravePreview: React.FC<EngravePreviewProps> = ({
                                                                settings,
                                                                isFrontFace = true,
                                                                zoomLevel = 1,
                                                                onValidationChange
                                                              }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // ズームレベルに応じてストラップ寸法をスケーリング
  const strap = useMemo(() => {
    return getScaledStrapDimensions(StrapDimensionsDefaults.FrontFace, zoomLevel);
  }, [zoomLevel]);

  // カスタムフックでCanvas描画を管理
  const {isLoading, validationResult} = useCanvasPreview(canvasRef, settings, strap, isFrontFace, onValidationChange);

  return (
    <div className="engrave-preview-container">
      <div
        className="canvas-wrapper"
        style={{
          position: 'relative',
          maxHeight: zoomLevel === ZOOM_SCALE_VALUE ? '600px' : 'none',
          overflow: zoomLevel === ZOOM_SCALE_VALUE ? 'auto' : 'visible',
          border: zoomLevel === ZOOM_SCALE_VALUE ? '1px solid #ddd' : 'none',
          borderRadius: '4px',
          display: 'inline-block'
        }}
      >
        <canvas
          ref={canvasRef}
          width={strap.canvasWidth}
          height={strap.canvasHeight}
          className="preview-canvas"
          style={{
            display: 'block',
            border: zoomLevel === ZOOM_SCALE_VALUE ? '1px solid #ccc' : 'none'
          }}
        />

        {isLoading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255,255,255,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px'
          }}>
            <div className="preview-loading">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">読み込み中...</span>
              </div>
              <p>プレビューを生成中...</p>
            </div>
          </div>
        )}
      </div>

      {!validationResult.isValid && validationResult.errors.length > 0 && (
        <div className="alert alert-danger mt-3">
          <strong>エラー:</strong>
          <ul className="list-unstyled">
            {validationResult.errors.map((error, index) => (
              <li key={index}>
                <i className="bi bi-x-circle-fill text-danger me-2"></i>
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="preview-info mt-3">
        <small className="text-muted">
          <p>
            <i className="bi bi-info-circle-fill"></i> <strong>プレビューはプレビューです</strong>
          </p>
          <p>
            実際の仕上がりとは若干の色味の違いがあります。<br/>
            <a href="https://rioil.booth.pm/items/6297784" target="_blank"
               rel="noopener noreferrer">BOOTHのサンプル画像</a>も参考にしてください。
          </p>
          <p>
            また、バターのサイズには個体差があるため、<br/>
            文字・画像の位置が少しずれる可能性があります。<br/>
          </p>
        </small>
      </div>
    </div>
  );
};
